import React from 'react';
import { makeStyles } from 'tss-react/mui';
import { useSelector } from 'react-redux';

import {
  Card,
  Typography,
  Grid,
  Badge,
  Button,
  Kind as BadgeKind,
  units,
} from '@hbf/dsl';
import { useIntl } from 'ha/i18n';

import { getIsAdmin, getIsAdvertiser } from 'ha/modules/AuthLogic/selectors';

import { getFilterTagsCount } from '../../utils/getFilterTagsCount';
import { SearchAlert } from '../SearchAlerts/SearchAlert';
import { SearchFilters } from './SearchFilters';
import { SearchFiltersContext } from '../../SearchFiltersContext';

const useStyles = makeStyles()(theme => ({
  card: {
    paddingTop: 0,
    paddingBottom: 0,
    // to not crop price filter slider labels
    overflow: 'initial',
  },
  titleWrapper: {
    padding: units.yx(1, 1.5),
  },
  header: {
    fontSize: units.rem(1.125),
  },
  badge: {
    backgroundColor: theme.palette.orange.main,
    marginLeft: theme.spacing(1),
  },
  searchAlert: {
    marginTop: theme.spacing(2),
  },
  clearButton: {
    textTransform: 'none',
    color: theme.palette.secondary.dark,
  },
}));

const SearchFiltersSidebar: React.FC = () => {
  const { T } = useIntl();
  const { classes } = useStyles();

  const isAdvertiser = useSelector(getIsAdvertiser);
  const isAdmin = useSelector(getIsAdmin);

  const { localState, clearFilters } = React.useContext(SearchFiltersContext);
  const tagsCount = getFilterTagsCount(localState);

  const handleClear = React.useCallback(() => {
    clearFilters();
  }, [clearFilters]);

  return (
    <Card
      className={classes.card}
      variant="outlined"
      data-test-locator="Search Filters Sidebar"
    >
      <Grid
        container
        className={classes.titleWrapper}
        justifyContent="space-between"
        alignItems="center"
      >
        <Grid item>
          <Badge
            kind={BadgeKind.inline}
            color="secondary"
            badgeClassName={classes.badge}
            badgeContent={tagsCount}
          >
            <Typography.H2 className={classes.header}>
              {T('Filters')}
            </Typography.H2>
          </Badge>
        </Grid>
        {tagsCount > 0 && (
          <Grid item>
            <Button
              variant="text"
              color="secondary"
              onClick={handleClear}
              className={classes.clearButton}
            >
              {T('cta.clear_all')}
            </Button>
          </Grid>
        )}
        {(!isAdvertiser || isAdmin) && (
          <Grid item xs={12} className={classes.searchAlert}>
            <SearchAlert color="primary" size="large" fullWidth />
          </Grid>
        )}
      </Grid>
      <SearchFilters withDateFilter />
    </Card>
  );
};

export { SearchFiltersSidebar };
// eslint-disable-next-line import/no-default-export
export default SearchFiltersSidebar;
